// InteliChat adapter: native conversation state (read-only + fetch trigger).
// Wraps the Vuex conversation + stats getters so InteliChat workspaces
// (Today, Inbox triage) read native truth without touching Vuex paths.
// NOTE: counts populate once the native stats/list fetch has run; this
// adapter never fabricates values - absent data reads as null.
import { computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import ConversationAPI from 'dashboard/api/inbox/conversation';

export function useInteliConversation() {
  const store = useStore();

  const stats = useMapGetter('conversationStats/getStats');
  const allConversations = useMapGetter('getAllConversations');
  const listLoadingStatus = useMapGetter('getChatListLoadingStatus');

  const mineCount = computed(() => {
    const count = stats.value?.mineCount;
    return typeof count === 'number' && count > 0 ? count : null;
  });

  const unassignedCount = computed(() => {
    const count = stats.value?.unAssignedCount;
    return typeof count === 'number' && count > 0 ? count : null;
  });

  const allCount = computed(() => {
    const count = stats.value?.allCount;
    return typeof count === 'number' && count > 0 ? count : null;
  });

  // The account's OPEN conversations as fetched by the native store
  // (the same list the native inbox renders - real data, most recent
  // activity first; a page at a time, exactly like the native list).
  // Conversations the never-miss HUD actively WATCHES: OPEN or PENDING. A
  // pending customer who is waiting still needs alerting just like an open
  // one (Tyler 2026-07-13), so pending is included here and loaded on mount
  // via ensureConversationsLoaded. (Name kept for its consumers.)
  const openConversations = computed(() =>
    (allConversations.value || []).filter(
      chat => chat.status === 'open' || chat.status === 'pending'
    )
  );

  // Trigger the native fetch machinery (same dispatches ChatList.vue
  // makes): open conversations across the account, newest activity
  // first, plus the real mine/unassigned/all tab counts from /meta.
  const conversationFetchParams = {
    assigneeType: 'all',
    status: 'open',
    sortBy: 'last_activity_at_desc',
    page: 1,
  };

  // Load PENDING conversations into the shared store WITHOUT disturbing the
  // agent's open-tab list filter or its stats: a direct read merged per-record
  // via updateConversation (a clean upsert). The agent's status-scoped list
  // does not surface these; only the never-miss buckets pick them up, and
  // real-time updates to them then flow through the store as usual.
  const loadPendingConversations = async () => {
    try {
      const response = await ConversationAPI.get({
        assigneeType: 'all',
        status: 'pending',
        sortBy: 'last_activity_at_desc',
        page: 1,
      });
      const list = response?.data?.data?.payload || [];
      list.forEach(chat => store.dispatch('updateConversation', chat));
    } catch (e) {
      // absent data reads as empty - never fabricate
    }
  };

  const ensureConversationsLoaded = async () => {
    store.dispatch('updateChatListFilters', conversationFetchParams);
    store.dispatch('conversationStats/get', conversationFetchParams);
    await store.dispatch('fetchAllConversations');
    await loadPendingConversations();
  };

  // ---- per-conversation derivations (all from native fields) ----
  const contactName = chat =>
    chat?.meta?.sender?.name || chat?.meta?.sender?.email || 'Customer';

  const assigneeName = chat =>
    chat?.meta?.assignee?.available_name || chat?.meta?.assignee?.name || '';

  const isUnassigned = chat => !chat?.meta?.assignee;

  // waiting_since is native truth: set when the customer sends a message,
  // cleared when an agent replies (epoch seconds; 0 = not waiting).
  const waitingSince = chat => Number(chat?.waiting_since || 0);

  const isAwaitingReply = chat => waitingSince(chat) > 0;

  // No agent has ever replied (native first_reply marker; the API
  // serializes nil as 0).
  const isUnattended = chat => !Number(chat?.first_reply_created_at || 0);

  const lastActivityAt = chat =>
    Number(chat?.last_activity_at || chat?.timestamp || 0);

  const lastMessage = chat => chat?.last_non_activity_message || null;

  return {
    stats,
    mineCount,
    unassignedCount,
    allCount,
    allConversations,
    openConversations,
    listLoadingStatus,
    ensureConversationsLoaded,
    contactName,
    assigneeName,
    isUnassigned,
    waitingSince,
    isAwaitingReply,
    isUnattended,
    lastActivityAt,
    lastMessage,
  };
}
